import Head from "next/head";
import CLMFooter from "components/CLM/CLMFooter";
import CLMNavbar from "components/CLM/CLMNavbar";
import Image from "next/image";

import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  CLMButtonPrimary,
  CLMButtonSecondary,
  CLMButtonSecondaryBorder,
} from "components/CLM/v3/Buttons";
import Link from "next/link";
import AnimatedLogos from "components/customers/AnimatedLogos";
import { BASE_ASSETS_URL } from "@constants/constants";
import FAQ2 from "components/CLM/FAQ2";
import CTASection from "components/CLM/CTASection";

const companyLogos = [
  {
    url: `${BASE_ASSETS_URL}/case-studies/angelfire.png`,
    name: "Angelfire",
    haveCaseStudy: true,
    companyKey: "angel-fire".toLowerCase(),
  },
  {
    url: `${BASE_ASSETS_URL}/case-studies/transport.png`,
    name: "G & H Transport",
    haveCaseStudy: true,
    companyKey: "g-h-transport".toLowerCase(),
  },

  {
    url: `${BASE_ASSETS_URL}/case-studies/beaver.png`,
    name: "Beaver",
    haveCaseStudy: true,
    companyKey: "beaver".toLowerCase(),
  },
  {
    url: `${BASE_ASSETS_URL}/case-studies/shine-above.png`,
    name: "Shine Above",
    haveCaseStudy: true,
    companyKey: "shine-above".toLowerCase(),
  },
  {
    url: `${BASE_ASSETS_URL}/case-studies/harry-corry.png`,
    name: "Harry Corry",
    haveCaseStudy: true,
    companyKey: "harry-corry-ltd".toLowerCase(),
  },
  {
    url: `${BASE_ASSETS_URL}/case-studies/rethink.png`,
    name: "Rethink",
    haveCaseStudy: true,
    companyKey: "rethink".toLowerCase(),
  },
  {
    url: `${BASE_ASSETS_URL}/case-studies/mancosa.png`,
    name: "Mancosa",
    haveCaseStudy: true,
    companyKey: "mancosa".toLowerCase(),
  },
  {
    url: `${BASE_ASSETS_URL}/case-studies/aging-comfort.png`,
    name: "Aging Comfort",
    haveCaseStudy: true,
    companyKey: "aging-comfort".toLowerCase(),
  },
];

function Dropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const dropdownList = [
    "All Categories",
    "CRM",
    "Clouad Storage",
    "Collaboration",
    "Email",
    "E-signing",
    "SSO",
    "Ticketing",
    "Systems",
    "Others",
  ];

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className="relative inline-block text-left w-full md:w-auto"
      ref={dropdownRef}
      onClick={() => setIsOpen(!isOpen)}
    >
      {/* Button */}
      <button
        type="button"
        // if isOpen false then rounded-b-xl
        className={`text-gray-500 border rounded-t-xl whitespace-nowrap font-medium  px-5 py-2.5 inline-flex items-center w-full ${
          isOpen ? "" : "rounded-b-xl"
        }`}
      >
        All Categories
        <ChevronDown className="w-6 h-6 ml-auto md:ml-2" />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-10 w-full md:w-44 bg-white rounded-lg shadow-sm divide-y divide-gray-100 dark:bg-gray-700">
          <ul className=" border rounded-b-xl">
            {dropdownList.map((item, index) => (
              <li
                key={index}
                className="px-4 py-2 text-gray-500 rounded hover:bg-gray-100"
              >
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const Integrations = () => {
  const automation = [
    {
      image: `${BASE_ASSETS_URL}/v2/clm/clm/integrations-zapier-2.webp`,
      heading: "Zapier",
      desc: "Connect BoloSign with 6000+ apps to automate your workflows and save time.",
    },
    {
      image: `${BASE_ASSETS_URL}/v2/clm/clm/integrations-pabbly.webp`,
      heading: "Pabbly",
      desc: "Connect BoloSign with Pabbly and do more with your forms and submissions.",
    },
    {
      image: `${BASE_ASSETS_URL}/v2/clm/clm/integrations-make.webp`,
      heading: "Make",
      desc: "Automate your work. Build something new. Trusted by thousands of fast-scaling organizations around the globe.",
    },
  ];

  const salesAndCrm = [
    {
      image: `${BASE_ASSETS_URL}/v2/clm/clm/integrations-pipedrive.webp`,
      heading: "Pipedrive",
      desc: "Pipedrive is a CRM-replacing sales pipeline management tool.",
    },
    {
      image: `${BASE_ASSETS_URL}/v2/clm/clm/integrations-crm.webp`,
      heading: "CRM",
      desc: "A CRM-replacing sales pipeline management tool.",
    },
    {
      image: `${BASE_ASSETS_URL}/v2/clm/clm/integrations-hubspot.webp`,
      heading: "HubSpot",
      desc: "HubSpot is the flexible, intuitive CRM system for your small business.",
    },
    {
      image: `${BASE_ASSETS_URL}/v2/clm/clm/integrations-airtable.webp`,
      heading: "AirTable",
      desc: "AirTable is the flexible, intuitive CRM system for your small business.",
    },
  ];

  const paymentGateways = [
    {
      image: `${BASE_ASSETS_URL}/v2/clm/clm/integrations-stripe.webp`,
      heading: "Stripe",
      desc: "Quick and secure way to accept credit/debit cards online.",
    },
  ];

  function Card({ image, heading, desc }) {
    return (
      // <div className=" rounded-2xl border min-h-[350px]  border-2 border-gray-400 p-5 hover:shadow-xl transition-shadow duration-300 cursor-pointer">
      <div className="px-6 py-8 rounded-xl border border-surface-dark bg-surface-light  transition-all duration-500 ease-in-out">
        <Image
          src={image}
          alt={heading}
          width={60}
          height={60}
          className="mb-12"
        />
        <h3 className="text-4xl font-semibold mb-4 text-gray-800">{heading}</h3>
        <p className="text-gray-800 mt-2 text-lg">{desc}</p>
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>Integrations - BoloSign CLM</title>
        <meta
          name="description"
          content="Integrate BoloSign CLM with your favorite tools to streamline contract management and boost productivity."
        />
        <meta
          name="keywords"
          content="BoloSign integrations, contract management integrations, CLM integrations, workflow automation, CRM integration, payment gateway integration, sales tools integration"
        />
        <link rel="canonical" href="https://www.bolosign.com/clm/integrations/" />
      </Head>
      <div className="">
        <div className="bg-gradient-to-tr from-indigo-600  to-purple-500 pb-12">
          <section className=" flex flex-col  items-center pb-0 text-white max-w-screen-xl mx-auto">
            <h1 className="text-3xl md:text-5xl xl:text-5xl font-semibold md:font-bold mt-10 lg:mt-20 xl:mt-40 text-center xl:w-[70%] md:w-[80%] w-[95%] z-10">
              Integrate with BoloSign for 2x faster, AI-powered contract
              closures
            </h1>
            <p className="mt-5 text-center xl:w-[65%] md:w-[75%] w-[95%] px-2 md:text-2xl text-xl mb-10 z-10">
              Connect your existing tools to create, track, and sign contracts
              faster with AI-powered efficiency built in
            </p>

            <div className=" z-10 mb-5">
              <CLMButtonSecondaryBorder
                buttonText="Schedule a demo"
                buttonUrl="/clm/request-demo"
              />
            </div>

            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/integrations-hero-bg-transparent.gif`}
              alt=""
              width={500}
              height={500}
              className="w-full h-full object-cover -mt-32  md:-mt-72 xl:-mt-[400px]"
            />
          </section>
        </div>
        {/* <AnimatedLogos companyLogos={companyLogos} /> */}

        {/* integrations section */}
        <div className="flex flex-col items-center justify-center max-w-screen-xl mx-auto">
          <section className="md:pt-32 pt-12 px-8">
            <h1 className="text-4xl md:text-5xl font-semibold mb-16 md:text-center text-start">
              Works smoothly with the tools your team already uses
            </h1>
            {/* div for dropdown and input */}
            {/* <div className="flex md:flex-row flex-col items-center gap-5 px-2">

              <div className="order-2 md:order-1 w-full md:w-auto">
                <Dropdown />
              </div>

              <input
                type="text"
                className="w-full outline-none text-sm text-gray-700 placeholder-gray-600 border p-3 px-4 md:ml-6 rounded-xl order-1 md:order-2"
                placeholder="Search integrations"
              />
            </div> */}

            {/* div for integrations card */}
            <h2 className="text-4xl font-semibold">Automation</h2>
            <div className="grid md:grid-cols-3 gap-5 my-10 mt-5">
              {automation.map((integration, index) => (
                <Card
                  key={index}
                  image={integration.image}
                  heading={integration.heading}
                  desc={integration.desc}
                />
              ))}
            </div>

            <h2 className="text-4xl font-semibold mb-3 pt-10">Sales & CRM</h2>
            <div className="grid md:grid-cols-3 gap-5 my-10 mt-5">
              {salesAndCrm.map((integration, index) => (
                <Card
                  key={index}
                  image={integration.image}
                  heading={integration.heading}
                  desc={integration.desc}
                />
              ))}
            </div>

            <h2 className="text-4xl font-semibold mb-3 pt-10">
              Payment Gateways
            </h2>
            <div className="grid md:grid-cols-3 gap-5 my-10 mt-5">
              {paymentGateways.map((integration, index) => (
                <Card
                  key={index}
                  image={integration.image}
                  heading={integration.heading}
                  desc={integration.desc}
                />
              ))}
            </div>
          </section>
        </div>
        {/* speak to counsellor section */}
        {/* <section className="bg-[#183794] pl-4">
          <div className="max-w-screen-xl mx-auto text-white flex items-center justify-between">
            <div className="md:pl-20 xl:pl-0 ">
              <h1 className="text-2xl md:text-4xl xl:text-5xl font-light mb-5">
                Speak to one of our experts for a personalized demo
              </h1>
              <Link
                href="request-demo"
                className="md:mt-10 mt-3 xl:px-8 px-6 xl:py-5 py-2 md:py-3 bg-white text-sm md:text-xl text-[#183794] rounded-md"
              >
                Schedule a demo
              </Link> 
            </div>
            <div className="w-full md:h-[400px] h-[200px]"> 
              <Image
                  src={`${BASE_ASSETS_URL}/v2/clm/clm/counsellor.webp`}
                alt=""
                className="w-full md:h-[400px] h-[200px] object-cover"
              />
            </div>
          </div>
        </section> */}
      </div>

      {/* <div className="text-center bg-black text-white py-16">
        <h2 className="text-4xl md:text-5xl mb-6">
          Get ready to Automate your Contracting Processes
        </h2>
        <CLMButtonPrimary
          buttonText="Contact Sales"
          buttonUrl="clm/request-demo"
        />
      </div> */}

      <div className="w-[100%] bg-dark py-16 relative mt-32">
        <h2 className="text-3xl font-semibold mb-3 text-white text-center">
          Trusted by
        </h2>
        <div className="pointer-events-none absolute left-0 top-0 h-full w-24 bg-gradient-to-r from-[#1b1b1b] to-transparent z-10 hidden md:block" />
        <div className="pointer-events-none absolute right-0 top-0 h-full w-24 bg-gradient-to-l from-[#1b1b1b] to-transparent z-10 hidden md:block" />
        <AnimatedLogos />
      </div>
    </>
  );
};

export default Integrations;
